import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, User, RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { LocationMap } from '@/components/LocationMap';
import type { LoginLocation } from '@/components/map/MapMarkers';

interface LoginLog {
  id: string;
  employee_id: string;
  employee_name: string;
  login_time: string;
  logout_time?: string;
  ip_address: string;
  location_lat?: number;
  location_lng?: number;
  location_address?: string;
  session_duration?: number;
}

export default function EmployeeLoginMapPage() {
  const [selectedLog, setSelectedLog] = useState<LoginLog | null>(null);

  // جلب سجلات تسجيل الدخول
  const { data: loginLogs = [], refetch, isLoading } = useQuery({
    queryKey: ["employee-login-logs"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from("employee_login_logs")
          .select(`
            *,
            employees:employee_id (
              name
            )
          `)
          .order("login_time", { ascending: false })
          .limit(100);
        
        if (error) {
          console.error('Error fetching login logs:', error);
          return [];
        }
        
        // تأكد من تنسيق البيانات بشكل صحيح حتى مع الخوانات المختلفة
        return (data || []).map(log => ({
          id: log.id,
          employee_id: log.employee_id,
          employee_name: log.employees?.name || 'غير محدد',
          login_time: log.login_time,
          logout_time: log.logout_time,
          ip_address: log.ip_address,
          location_lat: typeof log.location_lat === 'number' ? log.location_lat : 
                        (log.location_lat ? parseFloat(log.location_lat) : null),
          location_lng: typeof log.location_lng === 'number' ? log.location_lng : 
                        (log.location_lng ? parseFloat(log.location_lng) : null),
          location_address: log.location_address,
          session_duration: log.session_duration
        })) as LoginLog[];
      } catch (error) {
        console.error('Error in login logs query:', error);
        return [];
      }
    },
  });

  // تحويل LoginLog إلى LoginLocation للخريطة - مع ضمان وجود الإحداثيات
  const locationsWithCoords: LoginLocation[] = loginLogs
    .filter((log): log is LoginLog & { location_lat: number; location_lng: number } => 
      typeof log.location_lat === 'number' && typeof log.location_lng === 'number'
    )
    .map(log => ({
      id: log.id,
      employee_name: log.employee_name,
      location_lat: log.location_lat,
      location_lng: log.location_lng,
      location_address: log.location_address,
      login_time: log.login_time,
      logout_time: log.logout_time,
      ip_address: log.ip_address
    }));

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-green-600 rounded-lg flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">خريطة تسجيل الدخول</h1>
            <p className="text-gray-600">مراقبة مواقع وأوقات تسجيل دخول الموظفين والزوار</p>
          </div>
        </div>
        <Button onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 ml-2 ${isLoading ? 'animate-spin' : ''}`} />
          تحديث البيانات
        </Button>
      </div>

      {/* إحصائيات سريعة */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{loginLogs.length}</p>
                <p className="text-sm text-gray-600">إجمالي السجلات</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {loginLogs.filter(log => !log.logout_time).length}
                </p>
                <p className="text-sm text-gray-600">جلسات نشطة</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{locationsWithCoords.length}</p>
                <p className="text-sm text-gray-600">مواقع محددة</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-lg font-bold">
                  {Math.round(
                    loginLogs
                      .filter(log => log.session_duration)
                      .reduce((acc, log) => acc + (log.session_duration || 0), 0) / 
                    loginLogs.filter(log => log.session_duration).length || 0
                  )} د
                </p>
                <p className="text-sm text-gray-600">متوسط الجلسة</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* الخريطة التفاعلية */}
        <LocationMap locations={locationsWithCoords} />

        {/* جدول السجلات */}
        <Card>
          <CardHeader>
            <CardTitle>سجل تسجيل الدخول</CardTitle>
            <CardDescription>تفاصيل جلسات الموظفين ({loginLogs.length} سجل)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {loginLogs.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">لا توجد سجلات</p>
                </div>
              ) : (
                loginLogs.map((log) => (
                  <div 
                    key={log.id} 
                    className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                      selectedLog?.id === log.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                    }`}
                    onClick={() => setSelectedLog(log)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold">{log.employee_name}</h3>
                        <p className="text-sm text-gray-600">
                          {new Intl.DateTimeFormat('ar-SA', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).format(new Date(log.login_time))}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Badge className={log.logout_time ? 'bg-gray-100 text-gray-800' : 'bg-green-100 text-green-800'}>
                          {log.logout_time ? 'انتهت الجلسة' : 'نشط'}
                        </Badge>
                        {log.location_lat && log.location_lng && (
                          <Badge variant="outline" className="text-xs">
                            <MapPin className="w-3 h-3 mr-1" />
                            موقع محدد
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {log.location_address || 'موقع غير محدد'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          مدة الجلسة: {log.session_duration ? 
                            (log.session_duration > 60 ? 
                              `${Math.floor(log.session_duration / 60)} ساعة و ${log.session_duration % 60} دقيقة` : 
                              `${log.session_duration} دقيقة`
                            ) : 'جلسة نشطة'
                          }
                        </span>
                      </div>
                      
                      {log.ip_address && (
                        <div className="text-xs text-gray-500" dir="ltr">
                          IP: {log.ip_address}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
